import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { showSuccess, showError, showWarning, showInfo } from '../../utils/alert';
import { useTrusteesFooter, renderTrusteesHtml } from '../../components/reportTrusteesFooter';

function MemberCard() {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [selectedMember, setSelectedMember] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [coopProfile, setCoopProfile] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [systemConfig, setSystemConfig] = useState({});
  const [showPrintBookModal, setShowPrintBookModal] = useState(false);
  const [printBookType, setPrintBookType] = useState('POKOK_WAJIB'); // POKOK_WAJIB atau SUKARELA
  const [lastPrintedRow, setLastPrintedRow] = useState(0);
  const [isNewPage, setIsNewPage] = useState(false);
  const memberSelectRef = useRef(null);
  const [logoBase64, setLogoBase64] = useState('');
  const { trustees } = useTrusteesFooter();

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpeg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (e) { console.log('Failed to load logo') }
    }
    loadLogo();
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (user?.role === 'member' && user?.anggotaId) {
      const timeout = setTimeout(() => loadMemberCard(user.anggotaId), 500);
      return () => clearTimeout(timeout);
    } else if (searchParams.get('nba')) {
      const timeout = setTimeout(() => loadMemberCard(searchParams.get('nba')), 500);
      return () => clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    if (memberSelectRef.current && members.length > 0) {
      const $select = $(memberSelectRef.current);
      $select.select2({
        placeholder: '-- Pilih Anggota --',
        allowClear: true,
        width: '100%'
      }).on('change', (e) => {
        const nba = $(e.target).val();
        if (nba) loadMemberCard(nba);
      });
      return () => $select.off('change').select2('destroy');
    }
  }, [members]);

  const fetchInitialData = async () => {
    try {
      const [resMembers, resProfile, resConfig] = await Promise.all([
        api.get('/members?limit=1000&statusAktif=1'),
        api.get('/reports/profile-info'),
        api.get('/settings/config')
      ]);
      setMembers(resMembers.data.data || []);
      setCoopProfile(resProfile.data.data?.coopProfile || resProfile.data.data);
      setBranchInfo(resProfile.data.data?.branchInfo || resProfile.data.data);
      const groupedData = resConfig.data?.data?.data || resConfig.data?.data || {};
      const configObj = {};
      
      Object.values(groupedData).forEach(group => {
        if (typeof group === 'object' && group !== null) {
          Object.entries(group).forEach(([key, value]) => {
            configObj[key] = typeof value === 'object' ? value.value : value;
          });
        }
      });

      // Optional: incidental loan settings (may 403 for member role)
      try {
        const resIncidental = await api.get('/incidental-loans/settings');
        const incidentalSettings = resIncidental.data?.data?.[0] || {};
        if (incidentalSettings.KodeAkunPinjaman) {
          configObj.kode_akun_piutang_insidentil = incidentalSettings.KodeAkunPinjaman;
        }
        if (incidentalSettings.KodeAkunBunga) {
          configObj.kode_akun_piutang_bunga_insidentil = incidentalSettings.KodeAkunBunga;
        }
      } catch (e) { /* incidental settings not available for this role */ }
      
      console.log('Parsed systemConfig:', configObj);
      setSystemConfig(configObj);
    } catch (error) { console.error('Error:', error); }
  };

  const loadMemberCard = async (nba) => {
    setLoading(true);
    try {
      const response = await api.get(`/members/${nba}/card`);
      setCardData(response.data.data);
    } catch (error) { showError('Gagal memuat data'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val || 0);
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  const getTransactionType = (kode, ket) => {
    const k = (ket || '').toLowerCase();
    const kodePiutangAnggota = systemConfig?.['kode_akun_piutang_anggota'];
    const kodePiutangInsidentil = systemConfig?.['kode_akun_piutang_insidentil'];
    if (k.includes('pencairan') || (k.includes('pinjaman') && (kode === kodePiutangAnggota || kode === kodePiutangInsidentil))) return 'pencairan';
    if (k.includes('pembayaran') || k.includes('pelunasan') || k.includes('angs')) return 'pinjaman';
    return 'simpanan';
  };

  const getCombinedTransactions = () => {
    if (!cardData) return [];
    const jurnalData = cardData.jurnalDetailTransactions || [];
    let saldoPokok = 0, saldoWajib = 0, saldoSukarela = 0, saldoPinjaman = 0;
    const journalGroups = [];
    const seenJournals = new Set();
    
    jurnalData.forEach(tx => {
      if (!seenJournals.has(tx.NoJurnal)) {
        seenJournals.add(tx.NoJurnal);
        journalGroups.push({ noJurnal: tx.NoJurnal, tanggal: tx.TglJurnal, tipe: getTransactionType(tx.KodeAkun, tx.Keterangan), rows: [] });
      }
      journalGroups[journalGroups.length - 1].rows.push(tx);
    });

    return journalGroups.map(group => {
      let data = { tanggal: group.tanggal, keterangan: group.rows[0]?.Keterangan || '' };
      if (group.tipe === 'simpanan') {
        let kp = 0, dp = 0, kw = 0, dw = 0, ks = 0, ds = 0;
        const kodeSimpananPokok = systemConfig?.['kode_akun_simpanan_pokok'];
        const kodeSimpananWajib = systemConfig?.['kode_akun_simpanan_wajib'];
        const kodeSimpananSukarela = systemConfig?.['kode_akun_simpanan_sukarela'];
        group.rows.forEach(r => {
          const kode = r.KodeAkun?.toString();
          const d = parseFloat(r.Debet || 0), k = parseFloat(r.Kredit || 0);
          if (kodeSimpananPokok && kode === kodeSimpananPokok) { kp += k; dp += d; }
          else if (kodeSimpananWajib && kode === kodeSimpananWajib) { kw += k; dw += d; }
          else if (kodeSimpananSukarela && kode === kodeSimpananSukarela) { ks += k; ds += d; }
        });
        saldoPokok += (kp - dp); saldoWajib += (kw - dw); saldoSukarela += (ks - ds);
        return { ...data, tipe: 'simpanan', kreditPokok: kp, debetPokok: dp, kreditWajib: kw, debetWajib: dw, kreditSukarela: ks, debetSukarela: ds, saldoPokok, saldoWajib, saldoSukarela, saldoTotal: saldoPokok + saldoWajib + saldoSukarela, saldoPinjaman };
      }  
      if (group.tipe === 'pencairan') {
        const kodePiutangAnggota = systemConfig?.['kode_akun_piutang_anggota'];
        const kodePiutangInsidentil = systemConfig?.['kode_akun_piutang_insidentil'];
        let p = group.rows.reduce((acc, r) => {
          const kode = r.KodeAkun?.toString();
          if ((kodePiutangAnggota && kode === kodePiutangAnggota) || (kodePiutangInsidentil && kode === kodePiutangInsidentil)) {
            return acc + parseFloat(r.Debet || 0);
          }
          return acc;
        }, 0);
        saldoPinjaman += p;
        return { ...data, tipe: 'pencairan', pokok: p, saldoPinjaman, saldoPokok, saldoWajib, saldoSukarela, saldoTotal: saldoPokok + saldoWajib + saldoSukarela };
      }
      if (group.tipe === 'pinjaman') {
        let p = 0, b = 0, denda = 0;
        const kodePiutangAnggota = systemConfig?.['kode_akun_piutang_anggota'];
        const kodePiutangBunga = systemConfig?.['kode_akun_piutang_bunga'];
        const kodePiutangInsidentil = systemConfig?.['kode_akun_piutang_insidentil'];
        const kodePiutangBungaInsidentil = systemConfig?.['kode_akun_piutang_bunga_insidentil'];
        const kodePendapatanDenda = systemConfig?.['kode_akun_pendapatan_denda'];
        group.rows.forEach(r => {
          const k = r.KodeAkun?.toString(), ket = r.Keterangan?.toLowerCase();
          if ((kodePiutangAnggota && k === kodePiutangAnggota) || (kodePiutangInsidentil && k === kodePiutangInsidentil) || ket.includes('pokok')) p += parseFloat(r.Kredit || 0);
          else if (ket.includes('bunga') || (kodePiutangBunga && k === kodePiutangBunga) || (kodePiutangBungaInsidentil && k === kodePiutangBungaInsidentil)) b += parseFloat(r.Kredit || 0);
          else if (ket.includes('denda') || (kodePendapatanDenda && k === kodePendapatanDenda)) denda += parseFloat(r.Kredit || 0);
        });
        saldoPinjaman -= p;
        return { ...data, tipe: 'pinjaman', angsuran: p, bunga: b, denda, saldoPinjaman, saldoPokok, saldoWajib, saldoSukarela, saldoTotal: saldoPokok + saldoWajib + saldoSukarela };
      }
      return null;
    }).filter(Boolean);
  };

  const handlePrint = () => {
    const txs = getCombinedTransactions();
    const p = cardData.profilKeanggotaan;
    const pinj = cardData.profilPinjaman;
    const pinjList = cardData.profilPinjamanList;
    const sav = cardData.savings;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><style>
        body { font-family: Arial, sans-serif; font-size: 10px; padding: 20px; color: #333; }
        .kop-surat { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
        .kop-surat-inner { display: flex; align-items: center; gap: 15px; }
        .kop-logo { width: 70px; height: auto; }
        .kop-text { text-align: center; flex: 1; }
        .kop-nama { font-size: 20px; font-weight: bold; margin: 5px 0; }
        .kop-badan-hukum { font-size: 12px; margin: 3px 0; }
        .kop-alamat { font-size: 11px; margin: 2px 0; }
        .kop-kontak { font-size: 10px; margin: 2px 0; }
        .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 15px; }
        .section-title { background: #eee; padding: 5px; font-weight: bold; border-left: 3px solid #007bff; margin-top: 10px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 8px; }
        th, td { border: 1px solid #999; padding: 4px; text-align: right; }
        th { background: #444; color: #fff; text-align: center; }
        .text-left { text-align: left; }
      </style></head><body>
      <div class="kop-surat">
        <div class="kop-surat-inner">
          <img src="${window.location.origin}/logo.jpeg" class="kop-logo" alt="Logo" />
          <div class="kop-text">
            <div class="kop-nama">${coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'}</div>
            <div class="kop-badan-hukum">Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}</div>
            <div class="kop-alamat">${coopProfile?.Alamat || ''}</div>
            <div class="kop-kontak">Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}</div>
          </div>
        </div>
      </div>
      <h4 style="text-align: center; margin-bottom: 15px;">KARTU SIMPAN PINJAM ANGGOTA</h4>
      <div class="section-title">PROFIL & RINGKASAN DATA</div>
      <div class="grid">
        <div><strong>Profil Anggota:</strong><br>Nama: ${p.nama}<br>NBA: ${p.nba}<br>KTP: ${p.idKtp || '-'}<br>Telepon: ${p.telp || '-'}<br>Alamat: ${p.alamat || '-'}<br>Ahli Waris: ${p.ahliWaris1 || '-'}</div>
        <div><strong>Simpanan:</strong><br>Pokok: ${formatCurrency(sav?.POKOK?.saldo)}<br>Wajib: ${formatCurrency(sav?.WAJIB?.saldo)}<br>Sukarela: ${formatCurrency(sav?.SUKARELA?.saldo)}<br><strong>Total: ${formatCurrency((parseFloat(sav?.POKOK?.saldo) || 0) + (parseFloat(sav?.WAJIB?.saldo) || 0) + (parseFloat(sav?.SUKARELA?.saldo) || 0))}</strong></div>
        <div><strong>Pinjaman Aktif:</strong>${(pinjList || (pinj ? [pinj] : [])).map((pinjItem, idx) => `
          ${idx > 0 ? '<hr style="margin:5px 0">' : ''}
          <strong>${pinjItem.jenisPinjaman === 'Insidentil' ? 'Pinjaman Insidentil' : 'Pinjaman Reguler'}</strong><br>
          No: ${pinjItem.noPinjaman}<br>
          Plafond: ${formatCurrency(pinjItem.plafond)}${pinjItem.jenisPinjaman === 'Insidentil' ? `<br>Dicairkan: ${formatCurrency(pinjItem.jumlahDicairkan)}` : ''}<br>
          Sisa Pokok: ${formatCurrency(pinjItem.sisaPokok)}<br>
          Bunga: ${pinjItem.bunga}% (${pinjItem.tipeBunga})<br>
          Jatuh Tempo: ${formatDate(pinjItem.tglJatuhTempo)}
        `).join('')}</div>
      </div>
      <table>
        <thead>
          <tr><th rowspan="2">No</th><th rowspan="2">Tanggal</th><th colspan="3">Setoran</th><th colspan="3">Penarikan</th><th colspan="4">Saldo Simpanan</th><th colspan="5">Pinjaman</th></tr>
          <tr><th>SP</th><th>SW</th><th>SKR</th><th>SP</th><th>SW</th><th>SKR</th><th>SP</th><th>SW</th><th>SKR</th><th>Total</th><th>Pokok</th><th>Bunga</th><th>Angs</th><th>Denda</th><th>Saldo</th></tr>
        </thead>
        <tbody>
          ${txs.map((t, i) => `<tr><td style="text-align:center">${i+1}</td><td style="text-align:center">${formatDate(t.tanggal)}</td><td>${formatCurrency(t.kreditPokok)}</td><td>${formatCurrency(t.kreditWajib)}</td><td>${formatCurrency(t.kreditSukarela)}</td><td>${formatCurrency(t.debetPokok)}</td><td>${formatCurrency(t.debetWajib)}</td><td>${formatCurrency(t.debetSukarela)}</td><td>${formatCurrency(t.saldoPokok)}</td><td>${formatCurrency(t.saldoWajib)}</td><td>${formatCurrency(t.saldoSukarela)}</td><td style="font-weight:bold">${formatCurrency(t.saldoTotal)}</td><td>${formatCurrency(t.pokok)}</td><td>${formatCurrency(t.bunga)}</td><td>${formatCurrency(t.angsuran)}</td><td>${formatCurrency(t.denda)}</td><td style="font-weight:bold">${formatCurrency(t.saldoPinjaman)}</td></tr>`).join('')}
        </tbody>
      </table>
      ${renderTrusteesHtml(trustees)}
      </body></html>
    `);
    win.document.close(); win.print();
  };

  const handleExportXLS = () => {
    try {
      const txs = getCombinedTransactions() || [];
      const p = cardData?.profilKeanggotaan || {};
      const pinj = cardData?.profilPinjaman || {};
      const pinjList = cardData?.profilPinjamanList;
      const sav = cardData?.savings || {};

      // 1. Persiapkan data Header Profil (Baris Kosong untuk Jarak)
      const headerData = [
        [coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'],
        [`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`],
        [coopProfile?.Alamat || ''],
        [`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`],
        [],
        ['KARTU SIMPAN PINJAM ANGGOTA'],
        [],
        ['PROFIL ANGGOTA'],
        ['Nama', p.nama || '-', 'NBA', p.nba || '-', 'KTP', ` ${p.idKtp || '-'}`], // Spasi di depan KTP cegah format E+
        ['Alamat', p.alamat || '-', 'Telp', ` ${p.telp || '-'}`],
        ['Ahli Waris', p.ahliWaris1 || '-'],
        [],
        ['RINGKASAN SIMPANAN'],
        ['Pokok', sav?.POKOK?.saldo || 0, 'Wajib', sav?.WAJIB?.saldo || 0, 'Sukarela', sav?.SUKARELA?.saldo || 0, 'Total', (sav?.POKOK?.saldo || 0) + (sav?.WAJIB?.saldo || 0) + (sav?.SUKARELA?.saldo || 0)],
        [],
        ['INFORMASI PINJAMAN'],
        ...(pinjList || (pinj ? [pinj] : [])).flatMap((pinjItem, idx) => [
          ...(idx > 0 ? [['']] : []),
          [pinjItem.jenisPinjaman === 'Insidentil' ? 'Pinjaman Insidentil' : 'Pinjaman Reguler'],
          ['No', pinjItem.noPinjaman],
          ['Plafond', pinjItem.plafond || 0],
          ...(pinjItem.jenisPinjaman === 'Insidentil' ? [['Dicairkan', pinjItem.jumlahDicairkan || 0]] : []),
          ['Sisa Pokok', pinjItem.sisaPokok || 0],
          ['Bunga', `${pinjItem.bunga || 0}%`],
          ['Tipe', pinjItem.tipeBunga || '-'],
          ['Jatuh Tempo', formatDate(pinjItem.tglJatuhTempo)]
        ]),
        [],
        ['RIWAYAT TRANSAKSI'],
        [
          'No', 'Tanggal',
          'Set. SP', 'Set. SW', 'Set. SKR',
          'Tar. SP', 'Tar. SW', 'Tar. SKR',
          'Sal. SP', 'Sal. SW', 'Sal. SKR', 'Total Simp',
          'Pinj. Pokok', 'Pinj. Bunga', 'Pinj. Angs', 'Pinj. Dend', 'Pinj. Saldo'
        ]
      ];

      // 2. Mapping data tabel (Pastikan nilai undefined jadi 0)
      const bodyData = txs.map((t, i) => [
        i + 1,
        formatDate(t.tanggal),
        t.kreditPokok || 0, t.kreditWajib || 0, t.kreditSukarela || 0,
        t.debetPokok || 0, t.debetWajib || 0, t.debetSukarela || 0,
        t.saldoPokok || 0, t.saldoWajib || 0, t.saldoSukarela || 0, t.saldoTotal || 0,
        t.pokok || 0, t.bunga || 0, t.angsuran || 0, t.denda || 0, t.saldoPinjaman || 0
      ]);

      // Add trustees footer
      const footerData = [];
      if (trustees.filter(t => t.Kelompok === 'Pengurus').length) {
        footerData.push([]);
        footerData.push(['PENGURUS:']);
        trustees.filter(t => t.Kelompok === 'Pengurus').forEach(t => {
          footerData.push([t.Jabatan + ' : ' + t.NamaLengkap]);
        });
      }
      if (trustees.filter(t => t.Kelompok === 'Pengawas').length) {
        footerData.push([]);
        footerData.push(['PENGAWAS:']);
        trustees.filter(t => t.Kelompok === 'Pengawas').forEach(t => {
          footerData.push([t.Jabatan + ' : ' + t.NamaLengkap]);
        });
      }

      // Gabungkan Header dan Body
      const worksheetData = [...headerData, ...bodyData, ...footerData];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // 3. Atur Lebar Kolom (Agar tidak muncul #######)
      const wscols = [
        { wch: 5 },  { wch: 12 }, // No, Tanggal
        { wch: 10 }, { wch: 10 }, { wch: 10 }, // Setoran
        { wch: 10 }, { wch: 10 }, { wch: 10 }, // Penarikan
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, // Saldo
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 15 } // Pinjaman
      ];
      worksheet['!cols'] = wscols;

      // Buat Workbook dan Save
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Kartu Anggota');
      XLSX.writeFile(workbook, `KSPA_${p.nba || 'Export'}.xlsx`);

    } catch (err) {
      console.error("XLS Export Error:", err);
      showError("Gagal export Excel", "Pastikan library XLSX sudah terpasang.");
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const txs = getCombinedTransactions() || [];
      const p = cardData?.profilKeanggotaan || {};
      const pinj = cardData?.profilPinjaman || {};
      const pinjList = cardData?.profilPinjamanList;
      const sav = cardData?.savings || {};

      // Header Kop Surat
      const pageWidth = doc.internal.pageSize.width;
      try { doc.addImage(logoBase64, 'JPEG', 14, 5, 20, 20); } catch (e) { console.log('Logo PDF failed:', e.message) }
      doc.setFontSize(16).setFont(undefined, 'bold')
      doc.text(coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA', pageWidth / 2, 10, { align: 'center' })
      doc.setFontSize(9).setFont(undefined, 'normal')
      doc.text(`Badan Hukum No. : ${coopProfile?.NoBadanHukum || '-'}`, pageWidth / 2, 15, { align: 'center' })
      doc.setFontSize(8).text(coopProfile?.Alamat || '', pageWidth / 2, 20, { align: 'center' })
      doc.text(`Telp: ${coopProfile?.Telepon || '-'}  |  Email: ${coopProfile?.Email || '-'}  |  Website: ${coopProfile?.Website || '-'}  |  NPWP: ${coopProfile?.NPWP || '-'}`, pageWidth / 2, 25, { align: 'center' })
      doc.setLineWidth(0.5).line(10, 28, pageWidth - 10, 28)
      doc.setLineWidth(0.3).line(10, 29, pageWidth - 10, 29)
      doc.setFontSize(11).setFont(undefined, 'bold').text('KARTU SIMPAN PINJAM ANGGOTA', pageWidth / 2, 34, { align: 'center' })
      doc.setFontSize(8).setFont(undefined, 'normal')

      // --- Detail Profil ---
      let yPos = 38;
      const col1 = 14;
      const col2 = 100;
      const col3 = 180;

      // Fungsi pembantu agar tidak error jika data kosong
      const safeCurrency = (val) => formatCurrency(val || 0);

      // Baris 1
      doc.setFontSize(7).setFont(undefined, 'bold');
      doc.text(`Nama: ${p.nama || '-'} (${p.nba || '-'})`, col1, yPos);
      doc.text(`Simpanan Pokok: ${safeCurrency(sav?.POKOK?.saldo)}`, col2, yPos);
      doc.text(`Simpanan Wajib: ${safeCurrency(sav?.WAJIB?.saldo)}`, col3, yPos);

      // Baris 2
      yPos += 3.5;
      doc.setFont(undefined, 'normal');
      doc.text(`KTP: ${p.idKtp || '-'}`, col1, yPos);
      doc.text(`Simpanan Sukarela: ${safeCurrency(sav?.SUKARELA?.saldo)}`, col2, yPos);
      const totalSimp = (sav?.POKOK?.saldo || 0) + (sav?.WAJIB?.saldo || 0) + (sav?.SUKARELA?.saldo || 0);
      doc.text(`Total Simpanan: ${safeCurrency(totalSimp)}`, col3, yPos);

      // Baris 3
      yPos += 3.5;
      doc.text(`Alamat: ${p.alamat || '-'}`, col1, yPos);
      doc.text(`Ahli Waris: ${p.ahliWaris1 || '-'}`, col2, yPos);

      // Pinjaman - display all loans
      yPos += 3.5;
      const loansToDisplay = pinjList || (pinj ? [pinj] : []);
      if (loansToDisplay.length > 0) {
        loansToDisplay.forEach((pinjItem, idx) => {
          if (idx > 0) yPos += 3;
          doc.setFont(undefined, 'bold').text(`${pinjItem.jenisPinjaman === 'Insidentil' ? 'PINJAMAN INSIDENTIL' : 'PINJAMAN REGULER'}: ${pinjItem.noPinjaman}`, col1, yPos);
          doc.setFont(undefined, 'normal');
          yPos += 3;
          doc.text(`Plafond: ${formatCurrency(pinjItem.plafond)} | Sisa Pokok: ${formatCurrency(pinjItem.sisaPokok)} | Bunga: ${pinjItem.bunga}% | Jatuh Tempo: ${formatDate(pinjItem.tglJatuhTempo)}`, col1, yPos);
        });
      } else {
        doc.text('Tidak ada pinjaman aktif', col1, yPos);
      }

      // Helper untuk tabel agar tidak crash saat data null/undefined
      const cleanNum = (val) => {
        if (val === undefined || val === null || val === 0) return '-';
        return formatCurrency(val).replace('Rp', '').trim();
      };

      // Tabel Transaksi
      autoTable(doc, {
        startY: yPos + 4,
        margin: { left: 10, right: 10 },
        head: [
          [
            { content: 'No', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }, 
            { content: 'Tanggal', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }, 
            { content: 'Setoran', colSpan: 3, styles: { halign: 'center' } }, 
            { content: 'Penarikan', colSpan: 3, styles: { halign: 'center' } }, 
            { content: 'Saldo Simpanan', colSpan: 4, styles: { halign: 'center' } }, 
            { content: 'Pinjaman', colSpan: 5, styles: { halign: 'center' } }
          ],
          ['SP', 'SW', 'SKR', 'SP', 'SW', 'SKR', 'SP', 'SW', 'SKR', 'Total', 'Pokok', 'Bunga', 'Angs', 'Denda', 'Saldo']
        ],
        body: txs.map((t, i) => [
          i + 1, 
          formatDate(t.tanggal), 
          cleanNum(t.kreditPokok), cleanNum(t.kreditWajib), cleanNum(t.kreditSukarela),
          cleanNum(t.debetPokok), cleanNum(t.debetWajib), cleanNum(t.debetSukarela),
          cleanNum(t.saldoPokok), cleanNum(t.saldoWajib), cleanNum(t.saldoSukarela), cleanNum(t.saldoTotal),
          cleanNum(t.pokok), cleanNum(t.bunga), cleanNum(t.angsuran), cleanNum(t.denda), cleanNum(t.saldoPinjaman)
        ]),
        styles: { fontSize: 6, cellPadding: 0.8 },
        headStyles: { fillColor: [44, 62, 80], halign: 'center' },
        columnStyles: {
          0: { halign: 'center', cellWidth: 6 },
          1: { halign: 'center', cellWidth: 14 },
          2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' },
          5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' },
          8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' }, 
          11: { halign: 'right', fontStyle: 'bold' },
          12: { halign: 'right' }, 13: { halign: 'right' }, 14: { halign: 'right' }, 15: { halign: 'right' },
          16: { halign: 'right', fontStyle: 'bold' }
        },
        theme: 'grid',
        didParseCell: (data) => {
          if (data.column.index >= 2 && data.column.index <= 16 && data.section === 'body') {
            if (data.cell.text[0] !== '-') {
              data.cell.styles.halign = 'right';
            }
          }
        }
      });

      // Trustees footer
      if (trustees.length > 0) {
        doc.setFontSize(6);
        const midX = pageWidth / 2;
        
        const pengurus = trustees.filter(t => t.Kelompok === 'Pengurus');
        const pengawas = trustees.filter(t => t.Kelompok === 'Pengawas');
        const pengurusSub = pengurus.filter(t => t.Jabatan === 'Anggota/Panitia Kredit');
        const pengurusRest = pengurus.filter(t => t.Jabatan !== 'Anggota/Panitia Kredit');

        let yL = doc.lastAutoTable.finalY + 8;
        if (pengurus.length) {
          doc.setFont(undefined, 'bold');
          doc.text('PENGURUS:', 14, yL);
          yL += 3;
          doc.setFont(undefined, 'normal');
          pengurusRest.forEach(t => {
            doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, 19, yL);
            yL += 3;
          });
          if (pengurusSub.length) {
            doc.text('Anggota/Panitia Kredit :', 19, yL);
            yL += 3;
            pengurusSub.forEach((t, i) => {
              doc.text(`${i + 1}. ${t.NamaLengkap}`, 24, yL);
              yL += 3;
            });
          }
        }

        let yR = doc.lastAutoTable.finalY + 8;
        if (pengawas.length) {
          doc.setFont(undefined, 'bold');
          doc.text('PENGAWAS:', midX + 5, yR);
          yR += 3;
          doc.setFont(undefined, 'normal');
          pengawas.forEach(t => {
            doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, midX + 10, yR);
            yR += 3;
          });
        }
      }

      doc.save(`KSPA_${p.nba || 'Export'}.pdf`);
    } catch (err) {
      console.error("PDF Error:", err);
      showError("Terjadi kesalahan saat membuat PDF", "Pastikan data lengkap.");
    }
  };

  //Print Buku SP + SW
  const handlePrintBuku = () => {
    if (!cardData) return;
    const KODE_SIMPANAN_POKOK = systemConfig['kode_akun_simpanan_pokok'];
    const KODE_SIMPANAN_WAJIB = systemConfig['kode_akun_simpanan_wajib'];
    const txs = getCombinedTransactions();
    const MAX_ROWS = 33; 
    
    // 1. Gabungkan & Hitung Saldo Berjalan (Running Balance)
    let allBukuTxs = [];
    let runningTotalBalance = 0;

    txs.forEach(tx => {
      if (tx.kreditPokok > 0 || tx.debetPokok > 0) {
        const jumlah = tx.kreditPokok || tx.debetPokok;
        const tipe = tx.kreditPokok > 0 ? '01' : '02';
        runningTotalBalance += (tipe === '01' ? jumlah : -jumlah);
        allBukuTxs.push({
          tanggal: tx.tanggal, kode: KODE_SIMPANAN_POKOK.toString(), tipe: tipe, jumlah: jumlah, saldoKombinasi: runningTotalBalance
        });
      }
      if (tx.kreditWajib > 0 || tx.debetWajib > 0) {
        const jumlah = tx.kreditWajib || tx.debetWajib;
        const tipe = tx.kreditWajib > 0 ? '01' : '02';
        runningTotalBalance += (tipe === '01' ? jumlah : -jumlah);
        allBukuTxs.push({
          tanggal: tx.tanggal, kode: KODE_SIMPANAN_WAJIB.toString(), tipe: tipe, jumlah: jumlah, saldoKombinasi: runningTotalBalance
        });
      }
    });

    // --- PERINGATAN DATA KOSONG ---
    if (allBukuTxs.length === 0) {
      showWarning("Tidak ada data transaksi Simpanan Pokok atau Wajib untuk anggota ini.");
      return;
    }

    // 2. Tentukan Data & Posisi
    let printData = [];
    let currentPhysicalLine = 0; 
    let startFromIdx = 0;

    if (isNewPage) {
      const lastTx = allBukuTxs[allBukuTxs.length - 1];
      if (lastTx) {
        printData = [{ ...lastTx, isSaldoPindahan: true, displayNo: allBukuTxs.length }];
      }
      currentPhysicalLine = 0;
      startFromIdx = allBukuTxs.length; 
    } else {
      startFromIdx = parseInt(lastPrintedRow) || 0;
      
      // --- PERINGATAN NOMOR BARIS MELEBIHI DATA ---
      if (startFromIdx >= allBukuTxs.length) {
        showWarning(`Data sudah tercetak semua. Baris terakhir di sistem adalah nomor ${allBukuTxs.length}.`);
        return;
      }

      currentPhysicalLine = startFromIdx % MAX_ROWS;

      if (currentPhysicalLine === 0 && startFromIdx > 0) {
        const prevTx = allBukuTxs[startFromIdx - 1];
        printData.push({ ...prevTx, isSaldoPindahan: true, displayNo: startFromIdx });
      }

      const rawDataToPrint = allBukuTxs.slice(startFromIdx);
      rawDataToPrint.forEach((t, idx) => {
        printData.push({ ...t, displayNo: startFromIdx + idx + 1 });
      });
    }

    // --- VALIDASI AKHIR SEBELUM BUKA WINDOW ---
    if (printData.length === 0) {
      alert("Data yang akan dicetak kosong.");
      return;
    }

    // 3. Eksekusi Print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <style>
            @page { margin: 0; size: 140mm 100mm; }
            body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding-top: 5mm; }
            .line { display: flex; height: 4.5mm; align-items: center; white-space: pre; }
            .spacer { height: 4.5mm; visibility: hidden; }
            .no { width: 10mm; text-align: right; padding-right: 2mm; }
            .tgl { width: 25mm; }
            .kode { width: 12mm; }
            .tipe { width: 8mm; text-align: center; }
            .amt { width: 28mm; text-align: right; }
            .bal { width: 32mm; text-align: right; }
            .pindahan { font-weight: bold; }
          </style>
        </head>
        <body>
          ${Array(currentPhysicalLine).fill(0).map(() => `<div class="spacer"></div>`).join('')}
          ${printData.map((tx, idx) => {
            const isSld = tx.isSaldoPindahan;
            const linePos = currentPhysicalLine + idx + 1;
            const needBreak = linePos % MAX_ROWS === 0;
            return `
              <div class="line ${isSld ? 'pindahan' : ''}">
                <span class="no">${tx.displayNo}</span>
                <span class="tgl">${isSld ? '----------' : (tx.tanggal ? new Date(tx.tanggal).toISOString().split('T')[0] : '')}</span>
                <span class="kode">${isSld ? 'SLD' : tx.kode}</span>
                <span class="tipe">${isSld ? 'PF' : tx.tipe}</span>
                <span class="amt">${isSld ? 'PINDAHAN' : tx.jumlah.toLocaleString('id-ID')}</span>
                <span class="bal">${tx.saldoKombinasi.toLocaleString('id-ID')}</span>
              </div>
              ${needBreak ? '<div style="page-break-after: always; padding-top: 5mm;"></div>' : ''}
            `;
          }).join('')}
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      if (isNewPage) {
        setLastPrintedRow(allBukuTxs.length);
      } else {
        setLastPrintedRow(startFromIdx + (printData.length - (currentPhysicalLine === 0 && startFromIdx > 0 ? 1 : 0)));
      }
      setShowPrintBookModal(false);
      setIsNewPage(false); 
    }, 500);
  };

  // Print Buku Tabungan Sukarela
  const handlePrintBukuSukarela = () => {
    if (!cardData) return;
    

    const KODE_SIMPANAN_SUKARELA = systemConfig['kode_akun_simpanan_sukarela'];
    
    const txs = getCombinedTransactions();
    const MAX_ROWS = 33; 
    
    // 1. Gabungkan & Hitung Saldo Berjalan (Running Balance)
    let allBukuTxs = [];
    let runningTotalBalance = 0;

    txs.forEach(tx => {
      if (tx.kreditSukarela > 0 || tx.debetSukarela > 0) {
        const jumlah = tx.kreditSukarela || tx.debetSukarela;
        const tipe = tx.kreditSukarela > 0 ? '01' : '02';
        runningTotalBalance += (tipe === '01' ? jumlah : -jumlah);
        allBukuTxs.push({
          tanggal: tx.tanggal, kode: KODE_SIMPANAN_SUKARELA.toString(), tipe: tipe, jumlah: jumlah, saldoKombinasi: runningTotalBalance
        });
      }
    });

    // --- PERINGATAN DATA KOSONG ---
    if (allBukuTxs.length === 0) {
      showWarning("Tidak ada data transaksi Simpanan Sukarela untuk anggota ini.");
      return;
    }

    // 2. Tentukan Data & Posisi
    let printData = [];
    let currentPhysicalLine = 0; 
    let startFromIdx = 0;

    if (isNewPage) {
      const lastTx = allBukuTxs[allBukuTxs.length - 1];
      if (lastTx) {
        printData = [{ ...lastTx, isSaldoPindahan: true, displayNo: allBukuTxs.length }];
      }
      currentPhysicalLine = 0;
      startFromIdx = allBukuTxs.length; 
    } else {
      startFromIdx = parseInt(lastPrintedRow) || 0;
      
      // --- PERINGATAN NOMOR BARIS MELEBIHI DATA ---
      if (startFromIdx >= allBukuTxs.length) {
        alert(`Data sudah tercetak semua. Baris terakhir di sistem adalah nomor ${allBukuTxs.length}.`);
        return;
      }

      currentPhysicalLine = startFromIdx % MAX_ROWS;

      if (currentPhysicalLine === 0 && startFromIdx > 0) {
        const prevTx = allBukuTxs[startFromIdx - 1];
        printData.push({ ...prevTx, isSaldoPindahan: true, displayNo: startFromIdx });
      }

      const rawDataToPrint = allBukuTxs.slice(startFromIdx);
      rawDataToPrint.forEach((t, idx) => {
        printData.push({ ...t, displayNo: startFromIdx + idx + 1 });
      });
    }

    // --- VALIDASI AKHIR SEBELUM BUKA WINDOW ---
    if (printData.length === 0) {
      alert("Data yang akan dicetak kosong.");
      return;
    }

    // 3. Eksekusi Print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Buku Tabungan Sukarela</title>
          <style>
            @page { margin: 0; size: 140mm 100mm; }
            body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding-top: 5mm; }
            .line { display: flex; height: 4.5mm; align-items: center; white-space: pre; }
            .spacer { height: 4.5mm; visibility: hidden; }
            .no { width: 10mm; text-align: right; padding-right: 2mm; }
            .tgl { width: 25mm; }
            .kode { width: 12mm; }
            .tipe { width: 8mm; text-align: center; }
            .amt { width: 28mm; text-align: right; }
            .bal { width: 32mm; text-align: right; }
            .pindahan { font-weight: bold; }
          </style>
        </head>
        <body>
          ${Array(currentPhysicalLine).fill(0).map(() => `<div class="spacer"></div>`).join('')}
          ${printData.map((tx, idx) => {
            const isSld = tx.isSaldoPindahan;
            const linePos = currentPhysicalLine + idx + 1;
            const needBreak = linePos % MAX_ROWS === 0;
            return `
              <div class="line ${isSld ? 'pindahan' : ''}">
                <span class="no">${tx.displayNo}</span>
                <span class="tgl">${isSld ? '----------' : (tx.tanggal ? new Date(tx.tanggal).toISOString().split('T')[0] : '')}</span>
                <span class="kode">${isSld ? 'SLD' : tx.kode}</span>
                <span class="tipe">${isSld ? 'PF' : tx.tipe}</span>
                <span class="amt">${isSld ? 'PINDAHAN' : tx.jumlah.toLocaleString('id-ID')}</span>
                <span class="bal">${tx.saldoKombinasi.toLocaleString('id-ID')}</span>
              </div>
              ${needBreak ? '<div style="page-break-after: always; padding-top: 5mm;"></div>' : ''}
            `;
          }).join('')}
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      if (isNewPage) {
        setLastPrintedRow(allBukuTxs.length);
      } else {
        setLastPrintedRow(startFromIdx + (printData.length - (currentPhysicalLine === 0 && startFromIdx > 0 ? 1 : 0)));
      }
      setShowPrintBookModal(false);
      setIsNewPage(false); 
    }, 500);
  };

  // Print Buku Pinjaman
  const handlePrintBukuPinjaman = () => {
    if (!cardData) return;

    // Ambil kode akun secara dinamis dari systemConfig (Hasil hit ke /settings/config)
    const KODE_PIUTANG_ANGGOTA = systemConfig['kode_akun_piutang_anggota'];
    const KODE_PIUTANG_BUNGA = systemConfig['kode_akun_piutang_bunga'];
    const KODE_PENDAPATAN_DENDA = systemConfig['kode_akun_pendapatan_denda'];
    const KODE_PIUTANG_INSIDENTIL = systemConfig['kode_akun_piutang_insidentil'];
    const KODE_PIUTANG_BUNGA_INSIDENTIL = systemConfig['kode_akun_piutang_bunga_insidentil'];

    // Proteksi jika data config belum masuk
    if (!KODE_PIUTANG_ANGGOTA || !KODE_PIUTANG_BUNGA) {
      showError("Konfigurasi akun pinjaman belum dimuat", "Periksa koneksi atau database settings.");
      return;
    }

    // Get regular loan numbers to filter transactions
    const regularLoans = (cardData.profilPinjamanList || []).filter(p => p.jenisPinjaman === 'Regular');
    const regularLoanNumbers = regularLoans.map(p => p.noPinjaman);

    if (regularLoanNumbers.length === 0) {
      showWarning("Anggota ini tidak memiliki pinjaman reguler.");
      return;
    }

    const MAX_ROWS = 33;
    const jurnalData = cardData.jurnalDetailTransactions || [];
    let allBukuTxs = [];
    let runningPokokBalance = 0;

    // Mapping Data dari Jurnal ke Format Buku Tabungan
    jurnalData.forEach(row => {
      const kode = row.KodeAkun?.toString() || '';
      const debet = parseFloat(row.Debet || 0);
      const kredit = parseFloat(row.Kredit || 0);
      const tanggal = row.TglJurnal || row.Tanggal || '';

      // Check if this transaction uses regular loan account codes (not incidental)
      const isRegularTx = kode === KODE_PIUTANG_ANGGOTA.toString() ||
                         kode === KODE_PIUTANG_BUNGA.toString() ||
                         (KODE_PENDAPATAN_DENDA && kode === KODE_PENDAPATAN_DENDA.toString());

      // Exclude if it's using incidental loan codes
      if (KODE_PIUTANG_INSIDENTIL && kode === KODE_PIUTANG_INSIDENTIL.toString()) {
        return;
      }
      if (KODE_PIUTANG_BUNGA_INSIDENTIL && kode === KODE_PIUTANG_BUNGA_INSIDENTIL.toString()) {
        return;
      }

      if (!isRegularTx) return; // Skip non-regular transactions

      // 1. POKOK PINJAMAN (Mengubah Saldo Pokok)
      if (kode === KODE_PIUTANG_ANGGOTA.toString()) {
        if (debet > 0) { // Pencairan (Tipe 01)
          runningPokokBalance += debet;
          allBukuTxs.push({ tanggal, kode, tipe: '01', jumlah: debet, saldoKombinasi: runningPokokBalance });
        }
        if (kredit > 0) { // Angsuran Pokok (Tipe 02)
          runningPokokBalance -= kredit;
          allBukuTxs.push({ tanggal, kode, tipe: '02', jumlah: kredit, saldoKombinasi: runningPokokBalance });
        }
      }

      // 2. BUNGA (Tipe 03 - Tidak mengubah saldo pokok di buku)
      if (kode === KODE_PIUTANG_BUNGA.toString() && kredit > 0) {
        allBukuTxs.push({ tanggal, kode, tipe: '03', jumlah: kredit, saldoKombinasi: runningPokokBalance });
      }

      // 3. DENDA (Tipe 04 - Tidak mengubah saldo pokok di buku)
      if (KODE_PENDAPATAN_DENDA && kode === KODE_PENDAPATAN_DENDA.toString() && kredit > 0) {
        allBukuTxs.push({ tanggal, kode, tipe: '04', jumlah: kredit, saldoKombinasi: runningPokokBalance });
      }
    });

    // Validasi data akhir
    if (allBukuTxs.length === 0) {
      showWarning("Tidak ada transaksi pinjaman yang ditemukan.");
      return;
    }

    // Tentukan baris awal cetak
    let printData = [];
    let currentPhysicalLine = 0;
    let startFromIdx = 0;

    if (isNewPage) {
      const lastTx = allBukuTxs[allBukuTxs.length - 1];
      printData = [{ ...lastTx, isSaldoPindahan: true, displayNo: allBukuTxs.length }];
      currentPhysicalLine = 0;
      startFromIdx = allBukuTxs.length;
    } else {
      startFromIdx = parseInt(lastPrintedRow) || 0;
      if (startFromIdx >= allBukuTxs.length) {
        showWarning("Semua baris sudah tercetak.");
        return;
      }
      currentPhysicalLine = startFromIdx % MAX_ROWS;

      // Header saldo pindahan jika ganti halaman otomatis
      if (currentPhysicalLine === 0 && startFromIdx > 0) {
        printData.push({ ...allBukuTxs[startFromIdx - 1], isSaldoPindahan: true, displayNo: startFromIdx });
      }

      const rowsToPrint = allBukuTxs.slice(startFromIdx);
      rowsToPrint.forEach((t, idx) => {
        printData.push({ ...t, displayNo: startFromIdx + idx + 1 });
      });
    }

    // Eksekusi Print
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <style>
            @page { margin: 0; size: 140mm 100mm; }
            body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding-top: 5mm; }
            .line { display: flex; height: 4.5mm; align-items: center; white-space: pre; }
            .spacer { height: 4.5mm; visibility: hidden; }
            .no { width: 10mm; text-align: right; padding-right: 2mm; }
            .tgl { width: 25mm; }
            .kode { width: 12mm; }
            .tipe { width: 8mm; text-align: center; }
            .amt { width: 28mm; text-align: right; }
            .bal { width: 32mm; text-align: right; }
            .pindahan { font-weight: bold; }
          </style>
        </head>
        <body>
          ${Array(currentPhysicalLine).fill(0).map(() => `<div class="spacer"></div>`).join('')}
          ${printData.map(tx => `
            <div class="line ${tx.isSaldoPindahan ? 'pindahan' : ''}">
              <span class="no">${tx.displayNo}</span>
              <span class="tgl">${tx.isSaldoPindahan ? '----------' : (tx.tanggal ? new Date(tx.tanggal).toISOString().split('T')[0] : '')}</span>
              <span class="kode">${tx.isSaldoPindahan ? 'SLD' : tx.kode}</span>
              <span class="tipe">${tx.isSaldoPindahan ? 'PF' : tx.tipe}</span>
              <span class="amt">${tx.isSaldoPindahan ? 'PINDAHAN' : tx.jumlah.toLocaleString('id-ID')}</span>
              <span class="bal">${tx.saldoKombinasi.toLocaleString('id-ID')}</span>
            </div>
          `).join('')}
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      setLastPrintedRow(isNewPage ? allBukuTxs.length : startFromIdx + (printData.length - (currentPhysicalLine === 0 && startFromIdx > 0 ? 1 : 0)));
      setShowPrintBookModal(false);
      setIsNewPage(false); 
    }, 500);
  };

  // Print Buku Pinjaman Insidentil
  const handlePrintBukuPinjamanInsidentil = () => {
    if (!cardData) return;

    // Ambil kode akun secara dinamis dari systemConfig
    const KODE_PIUTANG_INSIDENTIL = systemConfig['kode_akun_piutang_insidentil'];
    const KODE_PIUTANG_BUNGA_INSIDENTIL = systemConfig['kode_akun_piutang_bunga_insidentil'];
    const KODE_PENDAPATAN_DENDA = systemConfig['kode_akun_pendapatan_denda'];

    // Proteksi jika data config belum masuk
    if (!KODE_PIUTANG_INSIDENTIL || !KODE_PIUTANG_BUNGA_INSIDENTIL) {
      showError("Konfigurasi akun pinjaman insidentil belum dimuat", "Periksa koneksi atau database settings.");
      return;
    }

    // Get incidental loan numbers to filter transactions
    const incidentalLoans = (cardData.profilPinjamanList || []).filter(p => p.jenisPinjaman === 'Insidentil');
    const incidentalLoanNumbers = incidentalLoans.map(p => p.noPinjaman);

    if (incidentalLoanNumbers.length === 0) {
      showWarning("Anggota ini tidak memiliki pinjaman insidentil.");
      return;
    }

    const MAX_ROWS = 33;
    const jurnalData = cardData.jurnalDetailTransactions || [];
    let allBukuTxs = [];
    let runningPokokBalance = 0;

    // Mapping Data dari Jurnal ke Format Buku Tabungan untuk pinjaman insidentil
    jurnalData.forEach(row => {
      const kode = row.KodeAkun?.toString() || '';
      const debet = parseFloat(row.Debet || 0);
      const kredit = parseFloat(row.Kredit || 0);
      const tanggal = row.TglJurnal || row.Tanggal || '';

      // Check if this transaction uses incidental loan account codes
      const isIncidentalTx = kode === KODE_PIUTANG_INSIDENTIL.toString() ||
                             kode === KODE_PIUTANG_BUNGA_INSIDENTIL.toString() ||
                             (KODE_PENDAPATAN_DENDA && kode === KODE_PENDAPATAN_DENDA.toString());

      if (!isIncidentalTx) return; // Skip non-incidental transactions

      // 1. POKOK PINJAMAN INSIDENTIL (Mengubah Saldo Pokok)
      if (kode === KODE_PIUTANG_INSIDENTIL.toString()) {
        if (debet > 0) { // Pencairan (Tipe 01)
          runningPokokBalance += debet;
          allBukuTxs.push({ tanggal, kode, tipe: '01', jumlah: debet, saldoKombinasi: runningPokokBalance });
        }
        if (kredit > 0) { // Angsuran Pokok (Tipe 02)
          runningPokokBalance -= kredit;
          allBukuTxs.push({ tanggal, kode, tipe: '02', jumlah: kredit, saldoKombinasi: runningPokokBalance });
        }
      }

      // 2. BUNGA (Tipe 03 - Tidak mengubah saldo pokok di buku)
      if (kode === KODE_PIUTANG_BUNGA_INSIDENTIL.toString() && kredit > 0) {
        allBukuTxs.push({ tanggal, kode, tipe: '03', jumlah: kredit, saldoKombinasi: runningPokokBalance });
      }

      // 3. DENDA (Tipe 04 - Tidak mengubah saldo pokok di buku)
      if (KODE_PENDAPATAN_DENDA && kode === KODE_PENDAPATAN_DENDA.toString() && kredit > 0) {
        allBukuTxs.push({ tanggal, kode, tipe: '04', jumlah: kredit, saldoKombinasi: runningPokokBalance });
      }
    });

    // Validasi data akhir
    if (allBukuTxs.length === 0) {
      showWarning("Tidak ada transaksi pinjaman insidentil yang ditemukan.");
      return;
    }

    // Tentukan baris awal cetak
    let printData = [];
    let currentPhysicalLine = 0;
    let startFromIdx = 0;

    if (isNewPage) {
      const lastTx = allBukuTxs[allBukuTxs.length - 1];
      printData = [{ ...lastTx, isSaldoPindahan: true, displayNo: allBukuTxs.length }];
      currentPhysicalLine = 0;
      startFromIdx = allBukuTxs.length;
    } else {
      startFromIdx = parseInt(lastPrintedRow) || 0;
      if (startFromIdx >= allBukuTxs.length) {
        showWarning("Semua baris sudah tercetak.");
        return;
      }
      currentPhysicalLine = startFromIdx % MAX_ROWS;

      // Header saldo pindahan jika ganti halaman otomatis
      if (currentPhysicalLine === 0 && startFromIdx > 0) {
        printData.push({ ...allBukuTxs[startFromIdx - 1], isSaldoPindahan: true, displayNo: startFromIdx });
      }

      const rowsToPrint = allBukuTxs.slice(startFromIdx);
      rowsToPrint.forEach((t, idx) => {
        printData.push({ ...t, displayNo: startFromIdx + idx + 1 });
      });
    }

    // Eksekusi Print
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <style>
            @page { margin: 0; size: 140mm 100mm; }
            body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding-top: 5mm; }
            .line { display: flex; height: 4.5mm; align-items: center; white-space: pre; }
            .spacer { height: 4.5mm; visibility: hidden; }
            .no { width: 10mm; text-align: right; padding-right: 2mm; }
            .tgl { width: 25mm; }
            .kode { width: 12mm; }
            .tipe { width: 8mm; text-align: center; }
            .amt { width: 28mm; text-align: right; }
            .saldo { width: 30mm; text-align: right; }
            .saldo-pindahan { background: #f0f0f0; }
          </style>
        </head>
        <body>
    `);

    printData.forEach((row) => {
      if (row.isSaldoPindahan) {
        printWindow.document.write(`
          <div class="line saldo-pindahan">
            <span class="no"></span><span class="tgl">SALDO PINDAHAN</span><span class="kode"></span><span class="tipe"></span><span class="amt"></span><span class="saldo">${formatCurrency(row.saldoKombinasi)}</span>
          </div>
        `);
      } else {
        const tglStr = row.tanggal ? row.tanggal.substring(0, 10) : '';
        printWindow.document.write(`
          <div class="line">
            <span class="no">${row.displayNo}</span><span class="tgl">${tglStr}</span><span class="kode">${row.kode}</span><span class="tipe">${row.tipe}</span><span class="amt">${formatCurrency(row.jumlah)}</span><span class="saldo">${formatCurrency(row.saldoKombinasi)}</span>
          </div>
        `);
      }
    });

    // Spacer untuk mengisi sisa halaman
    const remainingLines = MAX_ROWS - currentPhysicalLine - printData.length;
    for (let i = 0; i < remainingLines; i++) {
      printWindow.document.write(`<div class="spacer"></div>`);
    }

    printWindow.document.write('</body></html>');
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      setLastPrintedRow(isNewPage ? allBukuTxs.length : startFromIdx + (printData.length - (currentPhysicalLine === 0 && startFromIdx > 0 ? 1 : 0)));
      setShowPrintBookModal(false);
      setIsNewPage(false);
    }, 500);
  };

  const KODE_SIMPANAN_WAJIB = systemConfig['kode_akun_simpanan_wajib'];
  const KODE_SIMPANAN_POKOK = systemConfig['kode_akun_simpanan_pokok'];
  const KODE_SIMPANAN_SUKARELA = systemConfig['kode_akun_simpanan_sukarela'];
  const KODE_PIUTANG_ANGGOTA = systemConfig['kode_akun_piutang_anggota'];

  return (
    <div className="container-fluid p-3">
      <div className="card shadow-sm mb-4 no-print">
        <div className="card-header bg-gradient-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="m-0"><i className="fas fa-id-card mr-2"></i>Kartu Simpan Pinjam Anggota</h5>
          {cardData && (
            <div className="btn-group">
              <button className="btn btn-sm btn-light" onClick={handlePrint}><i className="fas fa-print mr-1"></i> Cetak</button>
              <button className="btn btn-sm btn-light text-success" onClick={handleExportXLS}><i className="fas fa-file-excel mr-1"></i> Excel</button>
              <button className="btn btn-sm btn-light text-danger" onClick={handleExportPDF}><i className="fas fa-file-pdf mr-1"></i> PDF</button>
              <button className="btn btn-sm btn-info" onClick={() => setShowPrintBookModal(true)}><i className="fas fa-book mr-1"></i> Cetak Buku</button>
            </div>
          )}
        </div>
        <div className="card-body bg-light text-center">
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <select ref={memberSelectRef} className="form-control select2">
              <option value="">-- Pilih Anggota --</option>
              {members.map(m => <option key={m.NBA} value={m.NBA}>{m.NBA} - {m.Nama}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="text-center p-5"><div className="spinner-border text-primary"></div></div>}

      {cardData && !loading && (
        <>
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-left-primary shadow-sm h-100 py-2">
                <div className="card-body">
                  <h6 className="font-weight-bold text-primary text-uppercase small">Profil Keanggotaan</h6>
                  <hr className="my-2"/>
                  <div className="small">
                    <div className="d-flex justify-content-between"><span>NBA:</span> <strong>{cardData.profilKeanggotaan?.nba}</strong></div>
                    <div className="d-flex justify-content-between"><span>Nama:</span> <strong>{cardData.profilKeanggotaan?.nama}</strong></div>
                    <div className="d-flex justify-content-between"><span>KTP:</span> <span>{cardData.profilKeanggotaan?.idKtp || '-'}</span></div>
                    <div className="d-flex justify-content-between"><span>Telp:</span> <span>{cardData.profilKeanggotaan?.telp || '-'}</span></div>
                    <div className="d-flex justify-content-between"><span>Alamat:</span> <span className="text-right" style={{maxWidth:'60%'}}>{cardData.profilKeanggotaan?.alamat}</span></div>
                    <div className="d-flex justify-content-between"><span>Ahli Waris:</span> <span>{cardData.profilKeanggotaan?.ahliWaris1 || '-'}</span></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-left-success shadow-sm h-100 py-2">
                <div className="card-body">
                  <h6 className="font-weight-bold text-success text-uppercase small">Ringkasan Simpanan</h6>
                  <hr className="my-2"/>
                  <div className="small">
                    <div className="d-flex justify-content-between"><span>Pokok:</span> <strong>{formatCurrency(cardData.savings?.POKOK?.saldo)}</strong></div>
                    <div className="d-flex justify-content-between"><span>Wajib:</span> <strong>{formatCurrency(cardData.savings?.WAJIB?.saldo)}</strong></div>
                    <div className="d-flex justify-content-between"><span>Sukarela:</span> <strong>{formatCurrency(cardData.savings?.SUKARELA?.saldo)}</strong></div>
                    <div className="d-flex justify-content-between border-top mt-2 pt-1"><span>Total:</span> <strong className="text-dark h6">{formatCurrency((parseFloat(cardData.savings?.POKOK?.saldo) || 0) + (parseFloat(cardData.savings?.WAJIB?.saldo) || 0) + (parseFloat(cardData.savings?.SUKARELA?.saldo) || 0))}</strong></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-left-warning shadow-sm h-100 py-2">
                <div className="card-body">
                  <h6 className="font-weight-bold text-warning text-uppercase small">Informasi Pinjaman</h6>
                  <hr className="my-2"/>
                  <div className="small">
                    {(cardData.profilPinjamanList || (cardData.profilPinjaman ? [cardData.profilPinjaman] : [])).map((pinj, idx) => (
                      <div key={idx} className={idx > 0 ? 'mt-3 pt-3 border-top' : ''}>
                        <div className="font-weight-bold text-primary mb-1">{pinj.jenisPinjaman === 'Insidentil' ? 'Pinjaman Insidentil' : 'Pinjaman Reguler'}</div>
                        <div className="d-flex justify-content-between"><span>No:</span> <span>{pinj.noPinjaman}</span></div>
                        <div className="d-flex justify-content-between"><span>Plafond:</span> <strong>{formatCurrency(pinj.plafond)}</strong></div>
                        {pinj.jenisPinjaman === 'Insidentil' && (
                          <div className="d-flex justify-content-between"><span>Dicairkan:</span> <strong>{formatCurrency(pinj.jumlahDicairkan)}</strong></div>
                        )}
                        <div className="d-flex justify-content-between"><span>Sisa Pokok:</span> <strong className="text-danger">{formatCurrency(pinj.sisaPokok)}</strong></div>
                        <div className="d-flex justify-content-between"><span>Bunga:</span> <span>{pinj.bunga}% ({pinj.tipeBunga})</span></div>
                        <div className="d-flex justify-content-between"><span>Jatuh Tempo:</span> <span>{formatDate(pinj.tglJatuhTempo)}</span></div>
                      </div>
                    ))}
                    {(!cardData.profilPinjamanList && !cardData.profilPinjaman) && (
                      <div className="text-muted">Tidak ada pinjaman aktif</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0">
            <div className="table-responsive">
              <table className="table table-sm table-hover table-bordered mb-0" style={{ fontSize: '11px' }}>
                <thead className="bg-dark text-white text-center">
                  <tr>
                    <th rowSpan="2" className="align-middle">No</th><th rowSpan="2" className="align-middle">Tanggal</th>
                    <th colSpan="3" className="bg-primary">Setoran</th><th colSpan="3" className="bg-danger">Penarikan</th>
                    <th colSpan="4" className="bg-success">Saldo Simpanan</th><th colSpan="5" className="bg-warning text-dark">Pinjaman</th>
                  </tr>
                  <tr className="small">
                    <th>SP</th><th>SW</th><th>SKR</th><th>SP</th><th>SW</th><th>SKR</th><th>SP</th><th>SW</th><th>SKR</th><th>Total</th><th>Pokok</th><th>Bunga</th><th>Angs</th><th>Denda</th><th>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {getCombinedTransactions().map((tx, idx) => (
                    <tr key={idx}>
                      <td className="text-center">{idx + 1}</td>
                      <td className="text-center">{formatDate(tx.tanggal)}</td>
                      <td className="text-right text-primary">{tx.kreditPokok > 0 ? formatCurrency(tx.kreditPokok) : '-'}</td>
                      <td className="text-right text-primary">{tx.kreditWajib > 0 ? formatCurrency(tx.kreditWajib) : '-'}</td>
                      <td className="text-right text-primary">{tx.kreditSukarela > 0 ? formatCurrency(tx.kreditSukarela) : '-'}</td>
                      <td className="text-right text-danger">{tx.debetPokok > 0 ? formatCurrency(tx.debetPokok) : '-'}</td>
                      <td className="text-right text-danger">{tx.debetWajib > 0 ? formatCurrency(tx.debetWajib) : '-'}</td>
                      <td className="text-right text-danger">{tx.debetSukarela > 0 ? formatCurrency(tx.debetSukarela) : '-'}</td>
                      <td className="text-right">{formatCurrency(tx.saldoPokok)}</td>
                      <td className="text-right">{formatCurrency(tx.saldoWajib)}</td>
                      <td className="text-right">{formatCurrency(tx.saldoSukarela)}</td>
                      <td className="text-right font-weight-bold">{formatCurrency(tx.saldoTotal)}</td>
                      <td className="text-right">{tx.pokok > 0 ? formatCurrency(tx.pokok) : '-'}</td>
                      <td className="text-right">{tx.bunga > 0 ? formatCurrency(tx.bunga) : '-'}</td>
                      <td className="text-right text-success">{tx.angsuran > 0 ? formatCurrency(tx.angsuran) : '-'}</td>
                      <td className="text-right text-danger">{tx.denda > 0 ? formatCurrency(tx.denda) : '-'}</td>
                      <td className="text-right font-weight-bold text-primary">{formatCurrency(tx.saldoPinjaman)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      
      {/* Modal Cetak Buku Tabungan */}
      {showPrintBookModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title"><i className="fas fa-book mr-2"></i>Cetak Buku Tabungan</h5>
                <button type="button" className="close" onClick={() => setShowPrintBookModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label><strong>Jenis Buku:</strong></label>
                  <select
                    className="form-control"
                    value={printBookType}
                    onChange={(e) => setPrintBookType(e.target.value)}
                  >
                    <option value="POKOK_WAJIB">Pokok & Wajib (Gabung)</option>
                    <option value="SUKARELA">Sukarela (Terpisah)</option>
                    <option value="PINJAMAN">Pinjaman Reguler</option>
                    <option value="PINJAMAN_INSIDENTIL">Pinjaman Insidentil</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label><strong>Nomor Baris Terakhir Tercetak:</strong></label>
                  <input 
                    type="number" 
                    className="form-control" 
                    value={lastPrintedRow} 
                    onChange={(e) => setLastPrintedRow(parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="0 = Cetak dari awal"
                  />
                  <small className="text-muted">
                    Masukkan 0 untuk cetak dari awal, atau nomor baris terakhir yang sudah tercetak
                  </small>
                </div>
                
                <div className="form-group">
                  <div className="custom-control custom-checkbox">
                    <input 
                      type="checkbox" 
                      className="custom-control-input" 
                      id="isNewPage"
                      checked={isNewPage}
                      onChange={(e) => setIsNewPage(e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="isNewPage">
                      <strong>Ganti Halaman Baru</strong> - Cetak hanya saldo terakhir
                    </label>
                  </div>
                </div>
                
                <div className="alert alert-info">
                  <small>
                    <strong>Format Cetak Simpanan:</strong><br/>
                    No | Tanggal | Kode | Tipe | Jumlah | Saldo<br/>
                    Kode: {KODE_SIMPANAN_WAJIB} = Wajib, {KODE_SIMPANAN_POKOK} = Pokok, {KODE_SIMPANAN_SUKARELA} = Sukarela<br/>
                    Tipe: 01=Setor, 02=Tarik<br/><br/>
                    <strong>Format Cetak Pinjaman:</strong><br/>
                    Kode: {KODE_PIUTANG_ANGGOTA}=Pinjaman<br/>
                    Tipe: 01=Pencairan, 02=Angsuran Pokok, 03=Bunga, 04=Denda<br/>
                    (Bunga & Denda tidak mengubah saldo pokok)
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPrintBookModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={printBookType === 'SUKARELA' ? handlePrintBukuSukarela : printBookType === 'PINJAMAN' ? handlePrintBukuPinjaman : printBookType === 'PINJAMAN_INSIDENTIL' ? handlePrintBukuPinjamanInsidentil : handlePrintBuku}
                >
                  <i className="fas fa-print mr-1"></i>
                  Cetak Buku {printBookType === 'SUKARELA' ? 'Sukarela' : printBookType === 'PINJAMAN' ? 'Pinjaman Reguler' : printBookType === 'PINJAMAN_INSIDENTIL' ? 'Pinjaman Insidentil' : 'Pokok+Wajib'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MemberCard;  
